import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import dataSearching from '../assets/Data_Searching.json';
import { fetchWorkerEvents } from '../api/workerEvents';
import { useEnvironment } from '../utils/EnvironmentContext.jsx';
import WorkerAttributeEventsTable from '../components/WorkerAttributeEventsTable';
import { passesFilters, getRowBackground } from '../helpers/workerEvents';
import { useFilteredPagination } from '../hooks/useFilteredPagination';
import DataTable from '../components/tables/DataTable';
import PaginationBar from '../components/common/PaginationBar';
import { buildWorkerEventsColumns } from '../tables/workerEventsColumns';
import '../styles/WorkerEventsPage.css';
import { FaUserCog } from 'react-icons/fa';
import WorkerActivitiesTimeline from '../components/workerEvents/WorkerActivitiesTimeline';
import { fetchWorkflowsAndQueues } from '../api/workflowsAndQueues';
import { extractSkillFromTargetWorkers, normalizeSkill } from '../helpers/workflows';
import TaskEventsModal from '../components/modals/TaskEventsModal';
import { getTaskInfo } from '../helpers/workerEvents';

export default function WorkerEventsPage() {
  const pageSize = 50; // kept same as original

  const [showAttributeEvents, setShowAttributeEvents] = useState(false);
  const [attributeEventsFetched, setAttributeEventsFetched] = useState(false);
  
  const [date, setDate] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('activities'); // table | activities
  const [selectedWorkers, setSelectedWorkers] = useState([]); // multi-select
  const [singleWorker, setSingleWorker] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [showMultiAgentModal, setShowMultiAgentModal] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, completedSlices: 0, totalSlices: 0, totalSoFar: 0 });
  const [streaming, setStreaming] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | requesting | retrieving | processing | done
  const [statusText, setStatusText] = useState('');
  const [filters, setFilters] = useState({
    sid: '',
    timestamp: '',
    eventType: '',
    worker: '',
    description: '',
    type: '',
    direction: '',
    queue: '',
    from: '',
    to: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { env } = useEnvironment();
  const [taskModal, setTaskModal] = useState({ open: false, taskSid: '', startDate: '' });
  const [queueSkillMap, setQueueSkillMap] = useState({}); // queueName -> normalizedSkill
  const [allowedQueues, setAllowedQueues] = useState(new Set()); // based on team filter
  const [stackCalls, setStackCalls] = useState(false);
  // Client filters: single-select and multi-select (modal)
  const [singleClient, setSingleClient] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showMultiClientModal, setShowMultiClientModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  // Handler to close modal
  const handleCloseModal = () => {
    setShowAttributeEvents(!showAttributeEvents);
  };

  const handleFetch = async () => {
    setLoading(true);
    setError('');
    setEvents([]);
    setProgress({ percent: 0, completedSlices: 0, totalSlices: 0, totalSoFar: 0 });
    setStreaming(true);
  setPhase('requesting');
  setStatusText('Worker‑events worden aangevraagd…');
    console.log('Fetching worker events for', { date, from, to, env });
    try {
      const { openProgress, fetchFinal } = await fetchWorkerEvents({ date, from, to, env });
      let es;
      await new Promise((resolve, reject) => {
        es = openProgress(
          (meta) => {
            setProgress(p => ({ ...p, totalSlices: meta.totalSlices }));
            setStatusText(`Worker‑events aangevraagd. Voorbereiden van ${meta.totalSlices} tijdvakken…`);
          },
          (p) => {
            // Keep UI in 'retrieving' until done; smooth percent so it doesn't jump straight to 100%
            setPhase('retrieving');
            const total = p.totalSlices || progress.totalSlices || 1;
            const completed = Math.min(p.completedSlices || 0, total);
            // Map slice progress to 0-92%, reserve 8% for processing
            const slicePercent = Math.floor((completed / total) * 92);
            setProgress({ percent: slicePercent, completedSlices: completed, totalSlices: total, totalSoFar: p.totalSoFar });
            setStatusText(`Worker‑events ophalen (${completed}/${total})…`);
          },
          async () => {
            try {
              // Switch to processing phase; leave a small buffer before 100%
              setPhase('processing');
              setStatusText('Worker‑events verwerken…');
              setProgress(prev => ({ ...prev, percent: Math.max(prev.percent, 95) }));
              const data = await fetchFinal();
              setEvents(data.events || []);
              setPhase('done');
              setStatusText('Klaar');
              setProgress(prev => ({ ...prev, percent: 100 }));
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          (err) => reject(err)
        );
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setStreaming(false);
      setTimeout(() => {
        // Clear status after short delay to keep UI clean
        setPhase('idle');
        setStatusText('');
      }, 1200);
    }
  };

  // (Removed legacy usePagination in favor of useFilteredPagination below)

  const uniqueWorkers = React.useMemo(() => (
    Array.from(new Set(events.map(ev => ev.worker).filter(Boolean))).sort()
  ), [events]);

  // Derive unique client/customer names from task events
  const uniqueClients = React.useMemo(() => {
    const set = new Set();
    for (const ev of events || []) {
      const ti = ev.taskInfo || {};
      let attrs = ti.attributes ?? ev.payload?.task_attributes ?? ev.payload?.attributes ?? {};
      if (typeof attrs === 'string') {
        try { attrs = JSON.parse(attrs); } catch { /* ignore */ }
      }
      const name = (
        attrs?.conversations?.conversation_attribute_3 ||
        attrs?.conversation_attribute_3 ||
        attrs?.customerName ||
        attrs?.customer_name ||
        attrs?.CustomerName ||
        attrs?.customers?.customer_attribute_2 ||
        attrs?.customer ||
        ''
      );
      if (typeof name === 'string') {
        const trimmed = name.trim();
        if (trimmed) set.add(trimmed);
      }
    }
    return Array.from(set).sort((a,b)=>a.localeCompare(b));
  }, [events]);

  // Workers filtered by selected team for agent selectors
  const filteredWorkers = React.useMemo(() => {
    if (!teamFilter) return uniqueWorkers;
    const set = new Set();
    for (const ev of events) {
      if (ev.eventType !== 'worker.activity.update') continue;
      const explicit = (ev.workerTeamName && typeof ev.workerTeamName === 'string') ? ev.workerTeamName.trim() : '';
      let teamName = explicit;
      if (!teamName) {
        const attrs = ev.workerAttributes || ev.activityOverview?.worker?.attributes || {};
        const raw = attrs?.team_name ?? attrs?.team ?? attrs?.teamName;
        teamName = (typeof raw === 'string' ? raw : '').trim();
      }
      if (teamName === teamFilter && ev.worker) set.add(ev.worker);
    }
    return Array.from(set).sort();
  }, [events, teamFilter, uniqueWorkers]);

  // Ensure selections stay consistent with team filter
  React.useEffect(() => {
    if (!teamFilter) return; // when cleared, keep selections
    if (singleWorker && !filteredWorkers.includes(singleWorker)) setSingleWorker('');
    if (selectedWorkers.length) {
      setSelectedWorkers(prev => prev.filter(w => filteredWorkers.includes(w)));
    }
  }, [teamFilter, filteredWorkers]);

  // Derive teams from worker attributes if present on activity events
  const uniqueTeams = React.useMemo(() => {
    const set = new Set();
    for (const ev of events) {
      if (ev.eventType !== 'worker.activity.update') continue;
      // Prefer explicit field from backend
      const explicit = (ev.workerTeamName && typeof ev.workerTeamName === 'string') ? ev.workerTeamName.trim() : '';
      if (explicit) { set.add(explicit); continue; }
      const attrs = ev.workerAttributes || ev.activityOverview?.worker?.attributes || {};
      const raw = attrs?.team_name ?? attrs?.team ?? attrs?.teamName;
      const name = (typeof raw === 'string' ? raw : '').trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort();
  }, [events]);

  // Load workflows/taskqueues once per env to derive queue -> required skill mapping
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchWorkflowsAndQueues(env);
        const qSkill = {};
        const taskQueues = Array.isArray(data?.taskQueues) ? data.taskQueues : (Array.isArray(data?.queues) ? data.queues : []);
        for (const q of taskQueues) {
          const skill = normalizeSkill(extractSkillFromTargetWorkers(q.targetWorkers));
          const name = q.friendlyName || q.friendly_name || q.sid;
          if (name) qSkill[name] = skill;
        }
        if (!cancelled) setQueueSkillMap(qSkill);
      } catch (e) {
        // non-fatal
        console.warn('Kon workflows/queues niet laden voor skills-koppeling', e);
      }
    })();
    return () => { cancelled = true; };
  }, [env]);

  // Compute visible team skills based on worker attribute snapshots inside events
  const visibleSkills = React.useMemo(() => {
    const skills = new Set();
    if (!teamFilter) return skills; // empty means no restriction
    for (const ev of events) {
      if (ev.eventType !== 'worker.activity.update') continue;
      const attrs = ev.workerAttributes || ev.activityOverview?.worker?.attributes || {};
      const rawTeam = (ev.workerTeamName && typeof ev.workerTeamName === 'string') ? ev.workerTeamName.trim() : (attrs?.team_name ?? attrs?.team ?? attrs?.teamName);
      const teamName = (typeof rawTeam === 'string' ? rawTeam : '').trim();
      if (teamName !== teamFilter) continue;
      const workerSkills = Array.isArray(attrs?.routing?.skills) ? attrs.routing.skills : [];
      for (const s of workerSkills) skills.add(normalizeSkill(s));
    }
    return skills;
  }, [events, teamFilter]);

  // Derive allowed queues from visible team skills and queueSkillMap
  useEffect(() => {
    if (!teamFilter) {
      setAllowedQueues(new Set());
      return;
    }
    const allowed = new Set();
    for (const [qName, qSkill] of Object.entries(queueSkillMap)) {
      if (!qSkill) continue;
      if (visibleSkills.has(qSkill)) allowed.add(qName);
    }
    setAllowedQueues(allowed);
  }, [teamFilter, queueSkillMap, visibleSkills]);

  const columns = React.useMemo(() => buildWorkerEventsColumns(uniqueWorkers), [uniqueWorkers]);

  // Filter predicate uses existing passesFilters (pre-pagination consistency retained)
  const predicate = React.useCallback((ev) => passesFilters(ev, filters), [filters]);
  const { pageItems, currentPage, totalPages, next, prev } = useFilteredPagination(events, pageSize, predicate);

  return (
    <div style={{ width: '100%', maxWidth: '100vw', overflowX: 'scroll' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h2>Agenten Activiteiten</h2>
          <div className="wep-filters">
            <div className="wep-control">
              <label className="wep-label">Datum</label>
              <input className="wep-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="wep-control">
              <label className="wep-label">Van</label>
              <input className="wep-input wep-input--sm" type="time" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="wep-control">
              <label className="wep-label">Tot</label>
              <input className="wep-input wep-input--sm" type="time" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            <button className="wep-button" onClick={handleFetch} disabled={loading || !date || !from || !to}>Ophalen</button>
          </div>
        </div>
  {/* Right side: button + weergave selector stacked (visible only after data fully loaded) */}
  {(events.length > 0 && !streaming) && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button className="wep-button wep-button--outline wep-button--icon" onClick={() => setShowAttributeEvents(!showAttributeEvents)}>
            <FaUserCog size={18} />
            <span>Historiek Skills-Wijzigingen</span>
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label className="wep-label" style={{ marginRight: 6 }}>Weergave</label>
            <select className="wep-input" style={{ width: 200 }} value={view} onChange={e => setView(e.target.value)}>
              <option value="table">Tabel</option>
              <option value="activities">Activiteitstijdlijn</option>
            </select>
          </div>
          {/* Modal for Worker Attribute Events */}
          {showAttributeEvents && (
            <div
              style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.4)',
                zIndex: 3000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 24,
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 4px 32px rgba(0,0,0,0.2)',
                  position: 'relative'
                }}
              >
                <button
                  onClick={handleCloseModal}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: '#eee',
                    border: 'none',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    cursor: 'pointer'
                  }}
                  aria-label="Sluiten"
                >
                  ×
                </button>
                <WorkerAttributeEventsTable
                  defaultDate={date}
                />
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      {(loading || streaming) && (
        <div style={{ margin: '0.5rem 0', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
            <Lottie
              animationData={dataSearching}
              loop
              autoplay
              style={{ width: 400, height: 400, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span>{statusText || 'Events ophalen…'}</span>
                <span>
                  {progress.percent}%
                  {progress.totalSlices > 0 && ` (${progress.completedSlices}/${progress.totalSlices})`}
                </span>
              </div>
              <div style={{ height: 12, background: '#eee', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(progress.percent || 5, 5)}%`, height: '100%', background: '#057fa2', transition: 'width 200ms ease' }} />
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Totaal tot nu toe opgehaald: {progress.totalSoFar}
              </div>
              {(phase === 'requesting' || phase === 'retrieving') && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  Grote tijdvakken kunnen even duren. Je kunt de app blijven gebruiken terwijl we je gegevens ophalen.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {error && <p style={{color:'red'}}>{error}</p>}
      {events.length > 0 && (
        <div style={{ width: '100%', overflowX: 'auto' }}>
          {view === 'table' && (
            <>
              <PaginationBar currentPage={currentPage} totalPages={totalPages} onPrev={prev} onNext={next} />
              <div style={{ marginTop: '1rem' }}>
                <DataTable
                  columns={columns}
                  data={pageItems}
                  filters={filters}
                  onFilterChange={setFilters}
                  getRowStyle={row => ({ background: getRowBackground(row.eventType) })}
                  onRowClick={(row) => {
                    const ti = getTaskInfo(row) || {};
                    const sid = ti.taskSid || ti.sid;
                    if (sid) {
                      // Use the table's current date as start point if available
                      setTaskModal({ open: true, taskSid: sid, startDate: date || undefined });
                    }
                  }}
                />
              </div>
              <PaginationBar currentPage={currentPage} totalPages={totalPages} onPrev={prev} onNext={next} />
            </>
          )}
          {view === 'activities' && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                {/* Team filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label className="wep-label">Team</label>
                  <select className="wep-input" style={{ width: 220 }} value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
                    <option value="">Alle teams</option>
                    {uniqueTeams.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Single agent select */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label className="wep-label">Agent</label>
                  <select className="wep-input" style={{ width: 260 }} value={singleWorker} onChange={e => setSingleWorker(e.target.value)}>
                    <option value="">Alle agenten</option>
                    {(teamFilter ? filteredWorkers : uniqueWorkers).map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>

                {/* Client/Customer name single select */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label className="wep-label">Klant</label>
                  <select className="wep-input" style={{ width: 260 }} value={singleClient} onChange={e => setSingleClient(e.target.value)}>
                    <option value="">Alle klanten</option>
                    {uniqueClients.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Multi agent modal trigger */}
                <button className="wep-button wep-button--outline" onClick={() => setShowMultiAgentModal(true)}>Filter meerdere agenten</button>
                {/* Multi client modal trigger */}
                <button className="wep-button wep-button--outline" onClick={() => setShowMultiClientModal(true)}>Selecteer meerdere klanten</button>
                {(singleWorker || selectedWorkers.length > 0 || teamFilter || singleClient || selectedClients.length > 0) && (
                  <button
                    className="wep-button wep-button--ghost"
                    onClick={() => {
                      setSingleWorker('');
                      setSelectedWorkers([]);
                      setTeamFilter('');
                      setSingleClient('');
                      setSelectedClients([]);
                    }}
                  >Reset filters</button>
                )}

                {/* Stack calls toggle */}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
                  <input type="checkbox" checked={stackCalls} onChange={e => setStackCalls(e.target.checked)} />
                  <span>Calls stapelen</span>
                </label>
              </div>

              {/* Multi-agent modal */}
              {showMultiAgentModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: '80vw', maxWidth: 1200, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.35)' }}>
                    <h3 style={{ marginTop: 0, marginBottom: 12 }}>Selecteer meerdere agenten</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                      {(teamFilter ? filteredWorkers : uniqueWorkers).map(w => (
                        <label key={w} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 6px' }}>
                          <input
                            type="checkbox"
                            checked={selectedWorkers.includes(w)}
                            onChange={(e) => {
                              setSelectedWorkers(prev => e.target.checked ? Array.from(new Set([...prev, w])) : prev.filter(x => x !== w));
                            }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                      <button className="wep-button wep-button--ghost" onClick={() => setShowMultiAgentModal(false)}>Sluiten</button>
                      <button className="wep-button" onClick={() => setShowMultiAgentModal(false)}>Toepassen</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Multi-client modal */}
              {showMultiClientModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: '80vw', maxWidth: 1200, maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.35)', position: 'relative', zIndex: 12001 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 12 }}>Selecteer meerdere klanten</h3>
                    {/* Search filter */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <input
                        type="text"
                        className="wep-input"
                        placeholder="Zoek klant…"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        style={{ width: 360 }}
                      />
                      {clientSearch && (
                        <button className="wep-button wep-button--ghost" onClick={() => setClientSearch('')}>Wissen</button>
                      )}
                    </div>
                    <div style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 160px)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                        {uniqueClients
                          .filter(c => !clientSearch || c.toLowerCase().includes(clientSearch.toLowerCase()))
                          .map(c => (
                        <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 6px' }}>
                          <input
                            type="checkbox"
                            checked={selectedClients.includes(c)}
                            onChange={(e) => {
                              setSelectedClients(prev => e.target.checked ? Array.from(new Set([...prev, c])) : prev.filter(x => x !== c));
                            }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c}</span>
                        </label>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                      <button className="wep-button wep-button--ghost" onClick={() => setShowMultiClientModal(false)}>Sluiten</button>
                      <button className="wep-button" onClick={() => setShowMultiClientModal(false)}>Toepassen</button>
                    </div>
                  </div>
                </div>
              )}

              <WorkerActivitiesTimeline
                events={events.filter(ev => {
                  // team filter (only for activity updates where we have attributes)
                  if (teamFilter) {
                    const teamName = (ev.workerTeamName && typeof ev.workerTeamName === 'string')
                      ? ev.workerTeamName.trim()
                      : (() => {
                          const attrs = ev.workerAttributes || ev.activityOverview?.worker?.attributes || {};
                          const raw = attrs?.team_name ?? attrs?.team ?? attrs?.teamName;
                          return (typeof raw === 'string' ? raw : '').trim();
                        })();
                    if (ev.eventType === 'worker.activity.update' && teamName !== teamFilter) return false;
                  }
                  // single worker takes precedence if set
                  if (singleWorker) return ev.worker === singleWorker;
                  // multi-select workers
                  if (selectedWorkers.length) return selectedWorkers.includes(ev.worker);
                  return true;
                })}
                onTaskClick={(sid) => setTaskModal({ open: true, taskSid: sid, startDate: date || undefined })}
                allowedQueues={allowedQueues}
                teamFilter={teamFilter}
                singleWorker={singleWorker}
                selectedWorkers={selectedWorkers}
                stackCalls={stackCalls}
                singleClient={singleClient}
                selectedClients={selectedClients}
              />
            </div>
          )}
        </div>
      )}
      {taskModal.open && (
        <TaskEventsModal
          taskSid={taskModal.taskSid}
          startDate={taskModal.startDate}
          onClose={() => setTaskModal({ open: false, taskSid: '', startDate: '' })}
        />
      )}
    </div>
  );
}
